'use client';

import { getCookie, setCookie } from 'cookies-next';
import { useRouter, usePathname } from 'next/navigation';

import { useIsCampaignPageType } from '@/app/providers/campaign-type';
import { Button } from '@/components/button';
import { Loader } from '@/components/loader';
import { ProductPlanCardCart } from '@/components/product-plan-card';
import {
  COMPARE_PLANS_URL,
  ENABLE_CAMPAIGNS,
  EXIT_MODAL_PATH_COOKIE,
  EXIT_MODAL_HREF_COOKIE,
} from '@/constants';
import { useCartPlans } from '@/lib/hooks/use-cart-plans';

import type { Dispatch, SetStateAction } from 'react';
import { EnergyType } from '@energy-retailer/constants';
import { useJoinFormStore } from '@/stores/join-form-store/join-form-store';

type CartPlanListingProps = {
  setIsCartDrawerOpen: Dispatch<SetStateAction<boolean>>;
};

export const CartPlanListing = ({
  setIsCartDrawerOpen,
}: CartPlanListingProps) => {
  const router = useRouter();
  const pathname = usePathname();
  const { setCurrentStep } = useJoinFormStore();
  const { gasPlan, electricityPlan, gasIsFetching, electricityIsFetching } = useCartPlans();
  const isCampaignPageType = useIsCampaignPageType();
  const slug = getCookie(EXIT_MODAL_PATH_COOKIE);

  if (gasIsFetching || electricityIsFetching) {
    return <Loader />;
  }

  const shouldShowExitModal = () => {
    if (slug) {
      setCookie(EXIT_MODAL_HREF_COOKIE, COMPARE_PLANS_URL as string);
      router.push(slug, { scroll: false });
      return true;
    }

    // isCampaignPageType is false for pages other than a campaign page, so this for a campaign page
    if (
      ENABLE_CAMPAIGNS &&
      isCampaignPageType &&
      !pathname.includes('/exit')
    ) {
      setCookie(EXIT_MODAL_HREF_COOKIE, COMPARE_PLANS_URL as string);
      router.push(`${pathname}/exit`, { scroll: false });
      return true;
    }

    return false;
  }

  return (
    <div className="flex flex-col gap-b">
      {electricityPlan ? (
        <ProductPlanCardCart
          key={electricityPlan.key}
          plan={electricityPlan}
          changePlan={() => {
            setIsCartDrawerOpen(false);

            if (shouldShowExitModal()) {
              return;
            }

            if (pathname !== COMPARE_PLANS_URL) {
              router.push(COMPARE_PLANS_URL);
            }
            setCurrentStep(EnergyType.Electricity);
          }}
        />
      ) : null}
      {gasPlan ? (
        <ProductPlanCardCart
          key={gasPlan.key}
          plan={gasPlan}
          changePlan={() => {
            setIsCartDrawerOpen(false);

            if (shouldShowExitModal()) {
              return;
            }

            if (pathname !== COMPARE_PLANS_URL) {
              router.push(COMPARE_PLANS_URL);
            }

            setCurrentStep(EnergyType.Gas);
          }}
        />
      ) : null}

      {!gasPlan && !electricityPlan ? (
        <Button
          variant="primary"
          size="default"
          className="w-full"
          onClick={() => {
            setIsCartDrawerOpen(false);

            // isCampaignPageType is false for pages other than a campaign page, so this for a campaign page
            if (
              ENABLE_CAMPAIGNS &&
              isCampaignPageType &&
              !pathname.includes('/exit')
            ) {
              setCookie(EXIT_MODAL_HREF_COOKIE, COMPARE_PLANS_URL as string);
              router.push(`${pathname}/exit`, { scroll: false });
              return;
            }

            router.push(COMPARE_PLANS_URL);
          }}
        >
          Get a quote
        </Button>
      ) : null}
    </div>
  );
};
